import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api, extractErrorMessage } from "@/api/client";
import { usePrava } from "@/api/hooks/useAuth";
import { vetaVlozenia } from "@/api/hooks/useRegal";
import { queryKeys } from "@/api/queryKeys";
import type { DataResponse, RegalStav, RegalUlozRequest, RegalVysledok } from "@/api/schemas";
import {
  VlozitDoZakazkyDialog,
  type CielRegalu,
  type RegalNaVlozenie,
  type StavVlozenia,
} from "@/components/tovar/VlozitDoZakazkyDialog";
import { VETA } from "@/lib/prava";

/** Správy stránky Regál CRC v režime `?rozpocet=1` (`stranka.js`, KOLO 237). */
type SpravaStranky =
  | { typ: "regal-crc:pripravena"; verzia: number }
  | ({ typ: "regal-crc:do-zakazky" } & RegalNaVlozenie)
  | ({ typ: "regal-crc:vlozit" } & RegalUlozRequest);

const ADRESA = "/regal/?rozpocet=1";
/** Stránka po `priprav` vloží hneď — keď do 20 s nič nepošle, niečo spadlo. */
const CAKANIE_MS = 20_000;

/**
 * Regály v hornom menu (KOLO 237). User: *„konfigurátor by mal vlastný tab… dizajn
 * je dobrý toho konfigurátora, aj ten cenník, že vieš otvoriť katalóg“* — stránka
 * Regál CRC (jeden zdroj s artifactom) v ráme cez celú plochu. Regál si pamätá
 * prehliadač ako samostatná stránka; **Vložiť do zákazky…** ho pošle do rozpočtu
 * tovaru vybranej zákazky (zákazku a rozpočet vyberie okno, stránka vloží ako
 * z rozpočtu tovaru — ten istý `PUT /varianty/{id}/regal`).
 */
export function RegalyPage(): JSX.Element {
  const ram = useRef<HTMLIFrameElement>(null);
  const prava = usePrava();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [regal, setRegal] = useState<RegalNaVlozenie | null>(null);
  const [stav, setStav] = useState<StavVlozenia>({ krok: "vyber" });
  const ciel = useRef<CielRegalu | null>(null);
  const casovac = useRef<number | null>(null);
  const smie = prava.tovar === "vsetko";

  const posli = useCallback((sprava: Record<string, unknown>) => {
    ram.current?.contentWindow?.postMessage(sprava, window.location.origin);
  }, []);
  // práva: rozpočtár (tovar len na čítanie) má Vložiť do zákazky sivé s dôvodom
  const posliPrava = useCallback(
    () => posli({ typ: "regal-crc:stav", iba_citanie: !smie, dovod: smie ? "" : VETA.tovar }),
    [posli, smie],
  );
  useEffect(() => {
    posliPrava();
  }, [posliPrava]);

  const zastavCakanie = (): void => {
    if (casovac.current !== null) window.clearTimeout(casovac.current);
    casovac.current = null;
  };
  useEffect(() => zastavCakanie, []);

  useEffect(() => {
    function na(e: MessageEvent<SpravaStranky>): void {
      if (e.origin !== window.location.origin || e.source !== ram.current?.contentWindow) return;
      const sprava = e.data;
      if (!sprava || typeof sprava !== "object" || !("typ" in sprava)) return;
      if (sprava.typ === "regal-crc:pripravena") {
        posliPrava();
      } else if (sprava.typ === "regal-crc:do-zakazky") {
        if (!smie) return;
        setStav({ krok: "vyber" });
        setRegal({ nazov: sprava.nazov, zostav: sprava.zostav, spolu: sprava.spolu });
      } else if (sprava.typ === "regal-crc:vlozit") {
        const c = ciel.current;
        if (!c || !smie) return;
        zastavCakanie();
        const { typ: _typ, ...telo } = sprava;
        api
          .put<DataResponse<RegalVysledok>>(`/varianty/${c.variantaId}/regal`, telo)
          .then(({ data }) => {
            const veta = vetaVlozenia(data.data);
            posli({ typ: "regal-crc:vlozene", ok: true, veta });
            setStav({ krok: "hotovo", ciel: c, veta });
            void qc.invalidateQueries({ queryKey: queryKeys.zakazky.lists() });
            void qc.invalidateQueries({ queryKey: queryKeys.zakazky.detail(c.zakazkaId) });
            void qc.invalidateQueries({ queryKey: ["regal", c.variantaId] });
            void qc.invalidateQueries({ queryKey: queryKeys.topsolidKoncepty.all });
          })
          .catch((err: unknown) => {
            const veta = extractErrorMessage(err);
            posli({ typ: "regal-crc:vlozene", ok: false, veta });
            setStav({ krok: "chyba", ciel: c, veta });
          });
      }
    }
    window.addEventListener("message", na);
    return () => window.removeEventListener("message", na);
  }, [posli, posliPrava, qc, smie]);

  async function vyber(c: CielRegalu): Promise<void> {
    ciel.current = c;
    setStav({ krok: "vkladam", ciel: c });
    try {
      // prefix a meno zákazky pre Určenie dielov Tandemu — ako v okne rozpočtu tovaru
      const { data } = await api.get<DataResponse<RegalStav>>(`/varianty/${c.variantaId}/regal`);
      posli({ typ: "regal-crc:priprav", prefix: data.data.prefix, zakazka: data.data.zakazka });
      zastavCakanie();
      casovac.current = window.setTimeout(() => {
        setStav({ krok: "chyba", ciel: c, veta: "Konfigurátor neodpovedal — skús Vložiť do zákazky znova." });
      }, CAKANIE_MS);
    } catch (err) {
      setStav({ krok: "chyba", ciel: c, veta: extractErrorMessage(err) });
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="regaly">
      <h1 className="sr-only">Regály CRC</h1>
      <iframe
        ref={ram}
        src={ADRESA}
        title="Konfigurátor regálu CRC"
        className="min-h-0 w-full flex-1 border-0"
        data-testid="regaly-ram"
      />
      {regal ? (
        <VlozitDoZakazkyDialog
          regal={regal}
          stav={stav}
          onVyber={(c) => void vyber(c)}
          onOtvor={(c) => navigate(`/zakazky/${c.zakazkaId}?varianta=${c.variantaId}`)}
          onSpat={() => setStav({ krok: "vyber" })}
          onClose={() => {
            zastavCakanie();
            // stránka mohla ostať na „Vkladám…“ (neodpovedala) — uvoľni jej tlačidlo
            if (stav.krok === "chyba") posli({ typ: "regal-crc:vlozene", ok: false });
            ciel.current = null;
            setRegal(null);
          }}
        />
      ) : null}
    </div>
  );
}
